import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatErr, fmtMoney, fmtDate } from "../../api";
import { SectionTitle, StatusPill, EmptyState, stripHtml } from "../../components/Atoms";
import RichEditor from "../../components/RichEditor";
import { Plus, PencilSimple, Trash, Stack, Check, Calendar, ListChecks, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// Project-level: title, client, category, description, dates, status
const PROJECT_EMPTY = { title:"", client_id:"", description:"", category:"", start_date:"", end_date:"", status:"Active" };
// Task-level: platform, reward, bonus, instructions, proof, deadline, status
const TASK_EMPTY = { title:"", description:"", platform:"Instagram", platforms:["Instagram"], reward_amount:0, bonus_amount:0,
  instructions:"", proof_required:"", evidence_required:"both", deadline:"", status:"Active" };
const PLATFORMS = ["Instagram","YouTube","LinkedIn","Offline","Other"];
const PROOF_OPTIONS = [
  { value:"link", label:"Link only", hint:"Creator must paste a submission URL" },
  { value:"file", label:"File only", hint:"Creator must upload a file (image / video / PDF)" },
  { value:"both", label:"Both link and file", hint:"Creator must provide a URL AND upload a file" },
];
const CATS = ["Fashion","F&B","Tech","Beauty","Lifestyle","Music","Fitness","Education"];

export default function ProjectsPage() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(PROJECT_EMPTY);
  const [open, setOpen] = useState(false);
  const [taskOpenProject, setTaskOpenProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState(TASK_EMPTY);
  const [editingTask, setEditingTask] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/projects"), api.get("/clients")]);
    setList(p.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open the tasks modal for a specific project when ?manage=<pid> is in the URL
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("manage");
    if (pid && list.length > 0 && !taskOpenProject) {
      setTaskOpenProject(pid);
      loadTasks(pid);
      setTaskForm(TASK_EMPTY);
      setEditingTask(null);
    }
  }, [location.search, list]);

  const loadTasks = async (pid) => { const { data } = await api.get(`/tasks?project_id=${pid}`); setTasks(data); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/projects/${editing}`, form);
      else await api.post("/projects", form);
      toast.success(editing ? "Project updated" : "Project created");
      setOpen(false); setEditing(null); setForm(PROJECT_EMPTY); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => { if (window.confirm("Delete project + tasks?")) { await api.delete(`/projects/${id}`); toast.success("Deleted"); load(); } };
  const complete = async (id) => { await api.post(`/projects/${id}/complete`); toast.success("Marked complete"); load(); };

  const saveTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...taskForm,
        project_id: taskOpenProject,
        reward_amount: Number(taskForm.reward_amount || 0),
        bonus_amount: Number(taskForm.bonus_amount || 0),
      };
      if (editingTask) await api.put(`/tasks/${editingTask}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editingTask ? "Task updated" : "Task added");
      setTaskForm(TASK_EMPTY); setEditingTask(null);
      loadTasks(taskOpenProject); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const editTask = (t) => {
    setEditingTask(t.id);
    const platforms = (t.platforms && t.platforms.length > 0) ? t.platforms : (t.platform ? [t.platform] : ["Instagram"]);
    setTaskForm({
      title: t.title || "", description: t.description || "",
      platform: platforms[0],
      platforms,
      reward_amount: t.reward_amount || 0, bonus_amount: t.bonus_amount || 0,
      instructions: t.instructions || "", proof_required: t.proof_required || "",
      evidence_required: t.evidence_required || "both",
      deadline: t.deadline || "", status: t.status || "Active",
    });
  };
  const delTask = async (id) => { await api.delete(`/tasks/${id}`); loadTasks(taskOpenProject); load(); };

  return (
    <div className="space-y-5">
      <SectionTitle title="Projects" subtitle="Campaigns running across all brands"
        action={<button onClick={() => { setEditing(null); setForm(PROJECT_EMPTY); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="new-project-btn"><Plus size={16} weight="bold" /> New</button>} />

      {list.length === 0 ? <EmptyState icon={Stack} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(p => (
            <div key={p.id} className="glass p-5 hover-lift" data-testid={`project-card-${p.id}`}>
              <Link to={`/admin/projects/${p.id}`} className="block" data-testid={`open-project-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate hover:text-[#22D3EE]">{p.title}</div>
                    <div className="text-xs text-muted truncate">{p.client_name} · {p.category || "—"}</div>
                  </div>
                  <StatusPill status={p.status} />
                </div>
                <div className="text-sm text-muted line-clamp-2 mb-3">{stripHtml(p.description)}</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Tasks</div><div className="text-white font-bold">{p.task_count || 0}</div></div>
                  <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Creators</div><div className="text-white font-bold">{p.ambassador_count || 0}</div></div>
                  <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Reward</div><div className="text-white font-bold">{fmtMoney(p.total_reward || 0)}</div></div>
                </div>
                <div className="text-[10px] text-muted mt-2 flex items-center justify-between">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</span>
                  <span className="text-[#22D3EE] flex items-center gap-1 font-bold">Open <ArrowRight size={12} weight="bold" /></span>
                </div>
              </Link>
              <div className="flex gap-2 mt-4">
                <button onClick={() => { setTaskOpenProject(p.id); loadTasks(p.id); setTaskForm(TASK_EMPTY); setEditingTask(null); }} className="btn-ghost flex-1 text-sm flex items-center justify-center gap-1.5" data-testid={`tasks-project-${p.id}`}><ListChecks size={14} /> Tasks</button>
                <button onClick={() => { setEditing(p.id); setForm({ ...PROJECT_EMPTY, ...p }); setOpen(true); }} className="btn-ghost text-sm" data-testid={`edit-project-${p.id}`}><PencilSimple size={14} /></button>
                <button onClick={() => complete(p.id)} className="btn-ghost text-[#A3E635] text-sm" data-testid={`complete-project-${p.id}`}><Check size={14} /></button>
                <button onClick={() => remove(p.id)} className="btn-ghost text-[#EC4899] text-sm" data-testid={`delete-project-${p.id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project create/edit modal */}
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <form onSubmit={save} className="glass relative p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3" onClick={e => e.stopPropagation()}>
            <div className="text-white text-xl font-bold mb-2">{editing ? "Edit Project" : "New Project"}</div>
            <div><label className="text-xs uppercase font-bold text-muted">Project Title</label><input required value={form.title} onChange={e => setForm({...form, title:e.target.value})} className="input-base mt-1" data-testid="project-input-title" /></div>
            <div><label className="text-xs uppercase font-bold text-muted">Brand / Client</label>
              <select required value={form.client_id} onChange={e => setForm({...form, client_id:e.target.value})} className="input-base mt-1" data-testid="project-input-client">
                <option value="">— select brand —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company}</option>)}
              </select>
            </div>
            <div><label className="text-xs uppercase font-bold text-muted">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category:e.target.value})} className="input-base mt-1">
                <option value="">—</option>{CATS.map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className="text-xs uppercase font-bold text-muted">Project Description</label>
              <RichEditor value={form.description} onChange={(v) => setForm({...form, description:v})} placeholder="Pitch the project — vibe, audience, deliverables." testid="project-desc-editor" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase font-bold text-muted">Start Date</label><input type="date" value={form.start_date || ""} onChange={e => setForm({...form, start_date:e.target.value})} className="input-base mt-1" /></div>
              <div><label className="text-xs uppercase font-bold text-muted">End Date</label><input type="date" value={form.end_date || ""} onChange={e => setForm({...form, end_date:e.target.value})} className="input-base mt-1" /></div>
            </div>
            <div><label className="text-xs uppercase font-bold text-muted">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status:e.target.value})} className="input-base mt-1">
                <option>Draft</option><option>Active</option><option>Paused</option><option>Completed</option>
              </select>
            </div>
            <div className="text-[11px] text-muted bg-white/5 rounded-xl px-3 py-2">
              💡 Reward, bonus, platform, deadline, and detailed instructions are now <strong className="text-white">set per task</strong>. After saving the project, open <strong className="text-white">Tasks</strong> on the card to add them.
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="save-project-btn">{editing ? "Save" : "Create"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Tasks management modal */}
      {taskOpenProject && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setTaskOpenProject(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="glass relative p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="text-white text-xl font-bold">Project Tasks</div>
              <button onClick={() => setTaskOpenProject(null)} className="text-muted hover:text-white"><X size={20} /></button>
            </div>

            <div className="space-y-2">
              {tasks.map(t => (
                <div key={t.id} className="p-3 rounded-xl bg-white/5" data-testid={`existing-task-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-white text-sm font-semibold truncate">{t.title}</div>
                        {((t.platforms && t.platforms.length > 0) ? t.platforms : (t.platform ? [t.platform] : [])).map(pl => (
                          <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full whitespace-nowrap">{pl}</span>
                        ))}
                        <StatusPill status={t.status || "Active"} />
                      </div>
                      <div className="text-[11px] text-muted mt-1 flex flex-wrap gap-3">
                        <span>Reward: <span className="text-white font-semibold">{fmtMoney(t.reward_amount)}</span></span>
                        {t.bonus_amount ? <span>Bonus: <span className="text-[#A3E635] font-semibold">{fmtMoney(t.bonus_amount)}</span></span> : null}
                        {t.deadline ? <span>Due: <span className="text-white">{fmtDate(t.deadline)}</span></span> : null}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => editTask(t)} className="btn-ghost text-xs" data-testid={`edit-task-${t.id}`}><PencilSimple size={12} weight="bold" /></button>
                      <button onClick={() => delTask(t.id)} className="btn-ghost text-[#EC4899] text-xs" data-testid={`delete-task-${t.id}`}><Trash size={12} weight="bold" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {tasks.length === 0 && <div className="text-muted text-sm">No tasks yet — add one below.</div>}
            </div>

            <form onSubmit={saveTask} className="space-y-2 border-t border-white/10 pt-3">
              <div className="text-xs uppercase font-bold text-muted tracking-wider">{editingTask ? "Edit task" : "Add new task"}</div>
              <input required placeholder="Task title" value={taskForm.title} onChange={e => setTaskForm({...taskForm, title:e.target.value})} className="input-base" data-testid="new-task-title" />
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase font-bold text-muted">Platforms <span className="text-[#9CA3AF] normal-case">(select one or more)</span></label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="new-task-platforms">
                    {PLATFORMS.map(p => {
                      const arr = Array.isArray(taskForm.platforms) ? taskForm.platforms : (taskForm.platform ? [taskForm.platform] : []);
                      const on = arr.includes(p);
                      return (
                        <label key={p} className={`px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer border transition ${on ? "bg-[#22D3EE]/20 border-[#22D3EE]/50 text-[#22D3EE]" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={e => {
                              const next = e.target.checked ? [...arr, p] : arr.filter(x => x !== p);
                              setTaskForm({ ...taskForm, platforms: next, platform: next[0] || "" });
                            }}
                            className="hidden"
                            data-testid={`new-task-platform-${p}`}
                          />
                          {p}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-muted">Status</label>
                  <select value={taskForm.status} onChange={e => setTaskForm({...taskForm, status:e.target.value})} className="input-base mt-1">
                    <option>Active</option><option>Paused</option><option>Closed</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-muted">Reward (₹)</label>
                  <input type="number" min="0" value={taskForm.reward_amount} onChange={e => setTaskForm({...taskForm, reward_amount:e.target.value})} className="input-base mt-1" data-testid="new-task-reward" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-muted">Bonus (₹)</label>
                  <input type="number" min="0" value={taskForm.bonus_amount} onChange={e => setTaskForm({...taskForm, bonus_amount:e.target.value})} className="input-base mt-1" data-testid="new-task-bonus" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase font-bold text-muted">Task Deadline</label>
                  <input type="date" value={taskForm.deadline || ""} onChange={e => setTaskForm({...taskForm, deadline:e.target.value})} className="input-base mt-1" data-testid="new-task-deadline" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-muted">Task Description / Detailed Instructions</label>
                <div className="mt-1"><RichEditor value={taskForm.instructions} onChange={(v) => setTaskForm({...taskForm, instructions:v})} placeholder="Step-by-step what the creator should do…" testid="new-task-instructions" /></div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-muted">Proof Type</label>
                <select value={taskForm.evidence_required || "both"} onChange={e => setTaskForm({...taskForm, evidence_required:e.target.value})} className="input-base mt-1" data-testid="new-task-evidence">
                  {PROOF_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className="text-[10px] text-muted mt-1">{(PROOF_OPTIONS.find(o => o.value === (taskForm.evidence_required || "both")) || {}).hint}</div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-muted">Proof Description</label>
                <textarea rows={2} value={taskForm.proof_required} onChange={e => setTaskForm({...taskForm, proof_required:e.target.value})} placeholder="e.g. Public Instagram reel URL + 24-hour insight screenshot" className="input-base mt-1" data-testid="new-task-proof" />
              </div>
              <div className="flex gap-2">
                {editingTask && (
                  <button type="button" onClick={() => { setEditingTask(null); setTaskForm(TASK_EMPTY); }} className="btn-ghost flex-1">Cancel edit</button>
                )}
                <button type="submit" className="btn-primary flex-1" data-testid="add-task-btn">{editingTask ? "Save task" : "+ Add task"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
